import React, { useState } from 'react';
import '../Styles/VisualDesigner.css';

const DEFAULT_VARS = {
    "--primary": "#a6203b",
    "--primary-dark": "#7a172b",
    "--bg-base": "#F5F5F5",
    "--bg-sidebar": "#FFFFFF",
    "--sk-topbar-grad": "linear-gradient(135deg, #a6203b, #7a172b)",
    "--text-main": "#1e293b",
    "--text-muted": "#64748b",
    "--surface": "#FFFFFF",
    "--border": "#e2e8f0"
};

const CATEGORIES = [
    { id: 'brand', label: 'Marca y Primarios', icon: '🎨' },
    { id: 'backgrounds', label: 'Fondos y Superficies', icon: '🖼️' },
    { id: 'texts', label: 'Textos y Bordes', icon: '✍️' },
];

const VARS_BY_CAT = {
    brand: [
        { key: "--primary", label: "Color Primario" },
        { key: "--primary-dark", label: "Color Primario Oscuro" },
        { key: "--sk-topbar-grad-1", label: "Topbar Inicio (Gradiente)", isGradient: true },
        { key: "--sk-topbar-grad-2", label: "Topbar Fin (Gradiente)", isGradient: true },
    ],
    backgrounds: [
        { key: "--bg-base", label: "Fondo de Aplicación" },
        { key: "--bg-sidebar", label: "Fondo de Sidebar" },
        { key: "--surface", label: "Superficie de Tarjetas" },
    ],
    texts: [
        { key: "--text-main", label: "Texto Principal" },
        { key: "--text-muted", label: "Texto Secundario" },
        { key: "--border", label: "Bordes y Divisores" },
    ]
};

export default function ThemeDesigner({ themeData, onSave, onCancel }) {
    const [activeCat, setActiveCat] = useState('brand');
    const [vars, setVars] = useState(() => {
        try {
            const parsed = typeof themeData?.valor === 'string' ? JSON.parse(themeData.valor) : {};
            // Handle gradient logic if stored as separate or composite
            const initialVars = { ...DEFAULT_VARS, ...parsed };
            return initialVars;
        } catch (e) {
            return DEFAULT_VARS;
        }
    });

    const [nombre, setNombre] = useState(themeData?.nombre || '');
    const [descripcion, setDescripcion] = useState(themeData?.descripcion || '');

    const updateVar = (key, value) => {
        setVars(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        const finalJson = JSON.stringify(vars);
        onSave({
            ...themeData,
            nombre,
            descripcion,
            valor: finalJson
        });
    };

    // Previsualización dinámica de los estilos
    const previewStyles = {
        "--color-primary": vars["--primary"],
        "--bg-app": vars["--bg-base"],
        "--bg-side": vars["--bg-sidebar"],
        "--text-color": vars["--text-main"],
        "--text-muted-color": vars["--text-muted"],
        "--card-bg": vars["--surface"],
        "--border-color": vars["--border"],
        "--grad-top": vars["--sk-topbar-grad"] || `linear-gradient(135deg, ${vars["--primary"]}, ${vars["--primary-dark"]})`
    };

    return (
        <div className="vd-container">
            {/* Sidebar Izquierda */}
            <div className="vd-sidebar">
                <div className="vd-sidebar-title">
                    <span>Diseñador de Temas</span>
                </div>
                <div className="vd-category-list">
                    {CATEGORIES.map(cat => (
                        <div
                            key={cat.id}
                            className={`vd-category-item ${activeCat === cat.id ? 'active' : ''}`}
                            onClick={() => setActiveCat(cat.id)}
                        >
                            <span>{cat.icon}</span>
                            {cat.label}
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                    <div className="vd-control-group">
                        <label className="vd-label">Nombre del Tema</label>
                        <input
                            className="roles-search"
                            style={{ width: '100%', marginBottom: '10px' }}
                            value={nombre}
                            onChange={e => setNombre(e.target.value)}
                            placeholder="Ej. SIGMA Premium"
                        />
                        <label className="vd-label">Descripción</label>
                        <textarea
                            className="roles-search"
                            style={{ width: '100%', minHeight: '60px', borderRadius: '8px' }}
                            value={descripcion}
                            onChange={e => setDescripcion(e.target.value)}
                            placeholder="Breve descripción..."
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn-cancel" onClick={onCancel} style={{ flex: 1 }}>Salir</button>
                        <button className="btn-action" onClick={handleSave} style={{ flex: 1 }}>Guardar</button>
                    </div>
                </div>
            </div>

            {/* Centro: Mockup Reactivo */}
            <div className="vd-main">
                <div className="vd-mockup-wrap" style={previewStyles}>
                    <div className="mock-shell" style={{ background: 'var(--bg-app)' }}>
                        {/* Mock Topbar */}
                        <div className="mock-topbar" style={{ background: 'var(--grad-top)', color: '#fff' }}>
                            <div style={{ fontWeight: 'bold' }}>SIGMA KEY</div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '15px', fontSize: '12px' }}>
                                <span>Dashboard</span>
                                <span>Reportes</span>
                                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }}></div>
                            </div>
                        </div>

                        <div className="mock-content">
                            {/* Mock Sidebar */}
                            <div className="mock-sidebar" style={{ background: 'var(--bg-side)', borderRight: '1px solid var(--border-color)' }}>
                                <div style={{ height: '10px', width: '80%', background: 'var(--color-primary)', borderRadius: '4px', opacity: 0.2 }}></div>
                                <div style={{ height: '10px', width: '60%', background: 'var(--text-muted-color)', borderRadius: '4px', opacity: 0.1 }}></div>
                                <div style={{ height: '10px', width: '70%', background: 'var(--text-muted-color)', borderRadius: '4px', opacity: 0.1 }}></div>
                                <div style={{ marginTop: '20px', height: '10px', width: '80%', background: 'var(--color-primary)', borderRadius: '4px' }}></div>
                                <div style={{ height: '10px', width: '70%', background: 'var(--text-muted-color)', borderRadius: '4px', opacity: 0.1 }}></div>
                            </div>

                            {/* Mock Workspace */}
                            <div className="mock-view">
                                <h4 style={{ color: 'var(--text-color)', marginBottom: '15px' }}>Vista Previa en Tiempo Real</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="mock-card" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                                        <div style={{ height: '8px', width: '40%', background: 'var(--color-primary)', marginBottom: '10px', borderRadius: '4px' }}></div>
                                        <div style={{ height: '6px', width: '90%', background: 'var(--text-muted-color)', marginBottom: '5px', borderRadius: '2px', opacity: 0.5 }}></div>
                                        <div style={{ height: '6px', width: '80%', background: 'var(--text-muted-color)', borderRadius: '2px', opacity: 0.5 }}></div>
                                        <button className="mock-btn" style={{ background: 'var(--color-primary)', color: '#fff', marginTop: '15px' }}>
                                            Botón Primario
                                        </button>
                                    </div>
                                    <div className="mock-card" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                                        <div style={{ height: '8px', width: '30%', background: 'var(--text-color)', marginBottom: '10px', borderRadius: '4px' }}></div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '3px', border: '1px solid var(--color-primary)' }}></div>
                                            <div style={{ height: '6px', width: '50%', background: 'var(--text-muted-color)', borderRadius: '2px' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <p style={{ marginTop: '20px', color: '#64748b', fontSize: '0.9rem' }}>
                    💡 Los cambios se verán reflejados aquí inmediatamente para que puedas diseñar tu tema ideal.
                </p>
            </div>

            {/* Panel Derecho: Controles */}
            <div className="vd-panel">
                <h4 style={{ marginBottom: '20px', color: '#1e293b' }}>Ajustar Colores</h4>
                {VARS_BY_CAT[activeCat].map(v => (
                    <div key={v.key} className="vd-control-group">
                        <label className="vd-label">{v.label}</label>
                        <div className="vd-color-input-wrap">
                            <div className="vd-color-preview" style={{ background: vars[v.key] || '#000' }}>
                                <input
                                    type="color"
                                    value={vars[v.key]?.startsWith('#') ? vars[v.key] : '#000000'}
                                    onChange={e => updateVar(v.key, e.target.value)}
                                />
                            </div>
                            <span className="vd-hex-text">{vars[v.key]}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
